import type { InventoryErrorBody, InventoryFieldError } from '../station/stationApiTypes';
import { objectLimitMessage } from '../../utils/objectLimitMessage';

/**
 * Read an inventory endpoint's failure into something a screen can render.
 *
 * The endpoints return four genuinely different 4xx payloads, each with a
 * different remedy, and collapsing them into one "something went wrong" is the
 * failure this exists to prevent:
 *
 *  - **400 `errors[]`** — every faulty entry in a bulk request, by index. An
 *    operator entering 40 compartments must see all their mistakes at once, not
 *    one per round-trip.
 *  - **400 `doors[]`** — positions already occupied at this station. Different
 *    fix: change the numbers, not the fields.
 *  - **409 `conflicts[]`** — objects that already fulfil another Product.
 *    Before #2227 this silently shrank the first Product's bookable pool, so
 *    the objects and the Product both have to be named.
 *  - **409 `blockers[]`** — the request is well-formed, the world is not ready
 *    (`held_by_live_rental`).
 *  - **409 `reason`** — the same shape for dividing a room (#1714): the
 *    request is fine, the room is not divisible *yet*, and which of the three
 *    refusals it was decides the remedy.
 *
 * RTK Query hands back `{ status, data }` with `status` **undefined** when no
 * response arrived at all (`utils/axiosBaseQuery.ts`), which is a fifth case:
 * unreachable, not rejected.
 */
export interface InventoryFailure {
    /** HTTP status, or undefined when nothing came back. */
    status?: number;
    /** True when there was no response at all — service down or wrong base URL. */
    unreachable: boolean;
    /** The backend's own message, when it sent one. */
    message?: string;
    fieldErrors: InventoryFieldError[];
    occupiedDoors: number[];
    conflicts: NonNullable<InventoryErrorBody['conflicts']>;
    blockers: string[];
    /** On retire: the future-booking check only applies to the last object. */
    isLastObjectOfProduct: boolean;
    missing: string[];
    /** Which of the divide refusals this was (#1714) — undefined for every
     *  other endpoint, and for a reason this build does not recognise. */
    reason?: string;
}

const asArray = <T>(value: unknown): T[] => (Array.isArray(value) ? (value as T[]) : []);

/** The server's own sentence: the body's `message`, or a plain-string body. */
const serverMessageOf = (body: InventoryErrorBody, raw: unknown): string | undefined => {
    if (typeof body.message === 'string') return body.message;
    return typeof raw === 'string' ? raw : undefined;
};

export const readInventoryFailure = (error: unknown): InventoryFailure => {
    const status = (error as { status?: unknown } | undefined)?.status;
    const raw = (error as { data?: unknown } | undefined)?.data;
    // The base query passes `err.response?.data || err.message` through, so the
    // body can be a plain string rather than the JSON shape.
    const body: InventoryErrorBody = (raw && typeof raw === 'object' ? raw : {}) as InventoryErrorBody;

    return {
        status: typeof status === 'number' ? status : undefined,
        unreachable: typeof status !== 'number',
        // The trial object limit (#2202) says how many and what to do, in the
        // operator's language, rather than the server's English sentence.
        message: objectLimitMessage(body) ?? serverMessageOf(body, raw),
        fieldErrors: asArray<InventoryFieldError>(body.errors),
        occupiedDoors: asArray<number>(body.doors),
        conflicts: asArray<NonNullable<InventoryErrorBody['conflicts']>[number]>(body.conflicts),
        blockers: asArray<string>(body.blockers),
        isLastObjectOfProduct: body.isLastObjectOfProduct === true,
        missing: asArray<string>(body.missing),
        ...(typeof body.reason === 'string' ? { reason: body.reason } : {}),
    };
};

/**
 * Does this failure carry anything specific enough to show on its own?
 *
 * Used to decide between rendering the detail lists and falling back to the
 * generic message — never to decide whether to show an error at all.
 */
export const hasInventoryDetail = (failure: InventoryFailure): boolean =>
    failure.fieldErrors.length > 0
    || failure.occupiedDoors.length > 0
    || failure.conflicts.length > 0
    || failure.blockers.length > 0
    || failure.missing.length > 0;
